import type {
  ContextDocument,
  KnowledgeSourceFile,
} from "./knowledge-sources.types";

type FileWithRelativePath = File & { webkitRelativePath?: string };
const KNOWLEDGE_PATH_FILENAME_PREFIX = "__sb_relpath__";

export function getFileRelativePath(file: File): string | undefined {
  const relativePath = (
    file as FileWithRelativePath
  ).webkitRelativePath?.trim();
  return relativePath ? relativePath : undefined;
}

/**
 * knowledgeUploadRelativePath decides where a picked file lands. With a folder
 * open it goes under that folder, keeping its own path from a folder pick; at
 * the root a plain file keeps no path at all.
 */
export function knowledgeUploadRelativePath(
  file: File,
  folderPath: string,
): string | undefined {
  const ownPath = getFileRelativePath(file);
  if (!folderPath) return ownPath;
  return `${folderPath}/${ownPath ?? file.name}`;
}

export function toKnowledgeUploadFileName(input: {
  fileName: string;
  relativePath?: string;
}): string {
  const normalizedRelativePath = normalizeKnowledgePath(input.relativePath);
  if (!normalizedRelativePath || !normalizedRelativePath.includes("/")) {
    return input.fileName;
  }

  return `${KNOWLEDGE_PATH_FILENAME_PREFIX}${encodeURIComponent(normalizedRelativePath)}`;
}

export function fromKnowledgeUploadFileName(fileName?: string): {
  name: string;
  relativePath?: string;
} {
  const normalizedFileName = fileName?.trim();
  if (!normalizedFileName) {
    return { name: "file" };
  }

  if (!normalizedFileName.startsWith(KNOWLEDGE_PATH_FILENAME_PREFIX)) {
    return { name: normalizedFileName };
  }

  const encodedRelativePath = normalizedFileName.slice(
    KNOWLEDGE_PATH_FILENAME_PREFIX.length,
  );

  try {
    const decodedRelativePath = normalizeKnowledgePath(
      decodeURIComponent(encodedRelativePath),
    );
    if (!decodedRelativePath) {
      return { name: normalizedFileName };
    }

    const name = decodedRelativePath.split("/").filter(Boolean).pop() ?? "file";
    return {
      name,
      relativePath: decodedRelativePath,
    };
  } catch {
    return { name: normalizedFileName };
  }
}

export function normalizeKnowledgePath(path?: string): string {
  if (!path) return "";

  return path
    .trim()
    .replaceAll("\\", "/")
    .replace(/^\.\/+/, "")
    .replaceAll(/\/+/g, "/")
    .toLowerCase();
}

/** Last segment of a folder path for UI copy — never the full nested path. */
export function knowledgeFolderLeafName(path: string): string {
  const segments = path.split("/").filter(Boolean);
  return segments.at(-1) ?? path;
}

export function getKnowledgeSourcePathKey(input: {
  name?: string;
  relativePath?: string;
}): string {
  return normalizeKnowledgePath(input.relativePath || input.name);
}

export function getKnowledgeSourcePathKeyFromSource(
  item: KnowledgeSourceFile | ContextDocument,
): string {
  if ("filename" in item) {
    return getKnowledgeSourcePathKey({
      name: item.filename,
      relativePath: item.relativePath,
    });
  }

  return getKnowledgeSourcePathKey({
    name: item.name,
    relativePath: item.relativePath,
  });
}
